import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Save, CreditCard, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const GATEWAYS = [
  { id: "cashfree", label: "Cashfree", envHint: "CASHFREE_CLIENT_ID, CASHFREE_CLIENT_SECRET, CASHFREE_ENVIRONMENT" },
  { id: "razorpay", label: "Razorpay", envHint: "RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET, RAZORPAY_WEBHOOK_SECRET" },
];

export default function AdminPaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [gateway, setGateway] = useState("cashfree");
  const [status, setStatus] = useState({ active_gateway: "cashfree", cashfree: {}, razorpay: {} });

  const load = async () => {
    const [s, g] = await Promise.all([
      api.get("/admin/payment-settings"),
      api.get("/admin/payment-gateway-status"),
    ]);
    setEnabled(!!s.data.enabled);
    setGateway(s.data.gateway || "cashfree");
    setStatus(g.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/payment-settings", { enabled, gateway });
      toast.success("Payment settings saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const activeConf = status[gateway] || {};

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-2">
        <CreditCard className="h-6 w-6 text-brand" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Payment Settings</h1>
          <p className="text-muted-foreground text-sm">Choose your gateway and control processing-fee collection.</p>
        </div>
      </div>

      <Card className="p-5 sm:p-6 ip-card-shadow space-y-4" data-testid="gateway-select-card">
        <div>
          <p className="font-semibold">Active payment gateway</p>
          <p className="text-xs text-muted-foreground">Customers pay the processing fee through the selected gateway.</p>
        </div>
        <RadioGroup value={gateway} onValueChange={setGateway} className="space-y-3">
          {GATEWAYS.map((gw) => {
            const conf = status[gw.id] || {};
            return (
              <label
                key={gw.id}
                htmlFor={`gw-${gw.id}`}
                className={`flex items-start gap-3 rounded-lg border-2 p-4 cursor-pointer transition-colors ${gateway === gw.id ? "border-brand bg-brand-light/40" : "border-border"}`}
                data-testid={`gateway-option-${gw.id}`}
              >
                <RadioGroupItem id={`gw-${gw.id}`} value={gw.id} className="mt-1" data-testid={`gateway-radio-${gw.id}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{gw.label}</span>
                    {conf.configured
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Configured · <span className="uppercase">{conf.mode}</span></span>
                      : <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Not configured</span>}
                  </div>
                  {!conf.configured && (
                    <p className="text-xs text-muted-foreground mt-1">Add <span className="font-mono">{gw.envHint}</span> in Emergent Secrets to enable.</p>
                  )}
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </Card>

      <Card className={`p-4 ip-card-shadow border-2 ${activeConf.configured ? "border-emerald-200" : "border-amber-200"}`} data-testid="gateway-status-card">
        <div className="flex items-start gap-3">
          {activeConf.configured
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
            : <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
          <div>
            <p className="font-semibold capitalize">{gateway} Gateway: {activeConf.configured ? "Configured" : "Not configured"} · Mode: <span className="uppercase">{activeConf.mode}</span></p>
            <p className="text-sm text-muted-foreground">
              {activeConf.configured
                ? "Server-side credentials are present. Customers can pay the processing fee."
                : "Add the gateway credentials in Emergent Secrets to enable live collection."}
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6 ip-card-shadow space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-semibold">Enable processing-fee payment</Label>
            <p className="text-xs text-muted-foreground">When on, approved customers can pay the processing fee online.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="payment-enabled-switch" />
        </div>

        <div className="flex gap-2 rounded-lg bg-brand-light p-3 text-xs text-brand">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          <p>The processing-fee amount is <b>never hard-coded</b>. It is taken from the <b>processing fee you set on each customer's active loan offer</b>. Update a customer's offer to change their payable amount.</p>
        </div>

        <Button onClick={save} disabled={busy} className="h-11" data-testid="payment-settings-save-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Settings
        </Button>
      </Card>
    </div>
  );
}
